import { NgIf } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { IPasswordComplexityRule } from 'app/shared/model/security.model';
import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';

export type PasswordStrengthConfig = {
    lowerCaseRule: boolean;
    upperCaseRule: boolean;
    digitRule: boolean;
    specialCharRule: boolean;
    lengthRule: boolean;
    customRegex?: string | undefined;
    customRegexMessage?: string | undefined;
};

const passwordStrengthConfigMapper = {
    '(?=.{8,})': 'lengthRule',
    '(?=.*\\d)': 'digitRule',
    '(?=.*[a-z])': 'lowerCaseRule',
    '(?=.*[A-Z])': 'upperCaseRule',
};

@Component({
    selector: 'auth-password-strength',
    templateUrl: './password-strength.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, MatPasswordStrengthModule],
})
export class PasswordStrengthComponent implements OnInit, OnDestroy {
    @Input() password: string | undefined;
    @Output() validEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    passwordPolicies: IPasswordComplexityRule[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    passwordStrengthConfig: PasswordStrengthConfig = {
        lowerCaseRule: false,
        upperCaseRule: false,
        digitRule: false,
        specialCharRule: false,
        lengthRule: false,
    };

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
    ) {
        this._authService
            .getPasswordRequirements()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (policies) => {
                    if (Array.isArray(policies) && policies.length > 0) {
                        this._mapToStrengthConfig(policies);
                    }
                },
            });
    }

    /**
     * On init
     */
    ngOnInit(): void {}

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onStrengthChanged(strengthNum: number) {
        const isValid = strengthNum === 100;
        this.validEvent.emit(isValid);
    }

    hasPasswordRequirements() {
        return Object.keys(this.passwordStrengthConfig).some((key) => {
            return !!this.passwordStrengthConfig[key];
        });
    }

    _mapToStrengthConfig(policies: IPasswordComplexityRule[]) {
        policies.forEach((policy) => {
            if (passwordStrengthConfigMapper[policy.regex]) {
                this.passwordStrengthConfig[
                    passwordStrengthConfigMapper[policy.regex]
                ] = true;
                return;
            }
            this.passwordStrengthConfig.customRegex = policy.regex;
            this.passwordStrengthConfig.customRegexMessage = policy.description;
        });
    }
}
